import React, { useEffect, useId, useMemo, useRef, useState } from "react";

function normalize(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Combobox with text filter for long city/region lists. */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  getOptionValue = (o) => String(o?.value ?? o?.id ?? ""),
  getOptionLabel = (o) => String(o?.label ?? o?.name ?? ""),
  placeholder = "",
  disabled = false,
  emptyMessage = "لا توجد نتائج",
  dir = "rtl",
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => options.find((o) => String(getOptionValue(o)) === String(value)),
    [options, value, getOptionValue]
  );

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    return options.filter((o) => normalize(getOptionLabel(o)).includes(q));
  }, [options, query, getOptionLabel]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (selected) {
      setQuery(getOptionLabel(selected));
    } else if (!value) {
      setQuery("");
    }
  }, [value, selected, getOptionLabel]);

  const inputStyle = {
    width: "100%",
    padding: "12px 12px",
    borderRadius: "10px",
    border: "1px solid #e5e5e5",
    background: disabled ? "#f5f5f5" : "#fff",
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }} dir={dir}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onChange("");
        }}
        onFocus={() => !disabled && setOpen(true)}
        style={inputStyle}
      />
      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 20,
            insetInline: 0,
            top: "calc(100% + 4px)",
            maxHeight: 220,
            overflowY: "auto",
            margin: 0,
            padding: 6,
            listStyle: "none",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
          }}
        >
          {filtered.length === 0 ? (
            <li
              style={{
                padding: "10px 12px",
                color: "#94a3b8",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {emptyMessage}
            </li>
          ) : (
            filtered.slice(0, 80).map((o) => {
              const optVal = getOptionValue(o);
              const active = String(optVal) === String(value);
              return (
                <li key={optVal} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(optVal);
                      setQuery(getOptionLabel(o));
                      setOpen(false);
                    }}
                    style={{
                      width: "100%",
                      textAlign: "start",
                      border: "none",
                      background: active ? "#f8fafc" : "transparent",
                      padding: "10px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: active ? 800 : 600,
                      color: "#0f172a",
                    }}
                  >
                    {getOptionLabel(o)}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
